/* EC Glow V3. Essential content is visible without entrance animations. */
(() => {
  'use strict';
  document.documentElement.classList.replace('no-js', 'js');
  const root = () => window.Shopify?.routes?.root || '/';
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const quantityValue = (input, value) => {
    const min = Number(input.min || 1);
    const step = Number(input.step || 1);
    const max = input.max === '' ? Infinity : Number(input.max);
    const upper = Number.isFinite(max) ? min + Math.floor((max - min) / step) * step : Infinity;
    return Math.min(upper, Math.max(min, min + Math.floor(((Number.isFinite(Number(value)) ? Number(value) : min) - min) / step) * step));
  };

  class ECProduct extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.sequence = 0;
      this.onChange = (event) => {
        if (event.target.matches('[data-option-choice]')) this.changeOption(event.target);
      };
      this.onSubmit = (event) => {
        if (event.target.matches('[data-product-form]')) this.addToCart(event);
      };
      this.onHistory = () => { if (!this.hasAttribute('data-quick-product')) this.loadProduct(new URL(location.href), false); };
      this.addEventListener('change', this.onChange);
      this.addEventListener('submit', this.onSubmit);
      window.addEventListener('popstate', this.onHistory);
      this.observePurchase();
    }

    disconnectedCallback() {
      this.request?.abort();
      this.purchaseObserver?.disconnect();
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('submit', this.onSubmit);
      window.removeEventListener('popstate', this.onHistory);
      this.bound = false;
    }

    observePurchase() {
      this.purchaseObserver?.disconnect();
      const form = this.querySelector('[data-product-form]');
      const bar = this.querySelector('[data-sticky-purchase]');
      if (!form || !bar || !('IntersectionObserver' in window)) return;
      this.purchaseObserver = new IntersectionObserver(([entry]) => {
        bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.bottom < 0);
      });
      this.purchaseObserver.observe(form);
    }

    setPending(pending) {
      this.pending = pending;
      this.setAttribute('aria-busy', String(pending));
      if (pending) {
        this.querySelectorAll('[data-add-button]').forEach(button => { button.disabled = true; });
        const id = this.querySelector('[name="id"]');
        if (id) id.disabled = true;
      }
      this.querySelectorAll('[data-payment-buttons]').forEach(node => {
        node.hidden = pending;
        node.inert = pending;
      });
    }

    async changeOption(input) {
      const selected = input.tagName === 'SELECT' ? input.selectedOptions[0] : input;
      const destination = new URL(selected.dataset.productUrl || this.dataset.productUrl, location.origin);
      if (destination.origin !== location.origin) return;
      const values = [...this.querySelectorAll('[data-option-choice]:checked, select[data-option-choice]')].map(choice => choice.value);
      destination.searchParams.set('option_values', values.join(','));
      // Preserve Shopify preview/theme parameters, but never carry a stale variant.
      const current = new URL(location.href);
      for (const key of ['preview_theme_id', '_fd', 'pb']) {
        if (current.searchParams.has(key)) destination.searchParams.set(key, current.searchParams.get(key));
      }
      await this.loadProduct(destination, !this.hasAttribute('data-quick-product'), input.id);
    }

    async loadProduct(destination, updateHistory, focusId) {
      this.request?.abort();
      this.request = new AbortController();
      const sequence = ++this.sequence;
      const quantity = this.querySelector('[name="quantity"]')?.value || 1;
      const status = this.querySelector('[data-update-status]');
      this.setPending(true);
      status.classList.remove('is-error');
      status.textContent = this.dataset.loadingMessage;
      const requestURL = new URL(destination);
      requestURL.searchParams.set('section_id', this.dataset.sectionId);
      try {
        const response = await fetch(requestURL, { signal: this.request.signal });
        if (!response.ok) throw new Error('Section request failed');
        const documentNext = new DOMParser().parseFromString(await response.text(), 'text/html');
        const next = documentNext.querySelector('ec-product');
        const content = next?.querySelector('[data-product-content]');
        if (!content || sequence !== this.sequence) {
          if (sequence === this.sequence) throw new Error('Missing product section');
          return;
        }
        // Replace the product's server-rendered state, not a guessed client-side variant map.
        // DOMParser parses noscript as markup; never import its fallback styles into a JS page.
        content.querySelectorAll('noscript').forEach(node => node.remove());
        this.querySelector('[data-product-content]').replaceWith(document.importNode(content, true));
        this.dataset.productUrl = next.dataset.productUrl;
        const input = this.querySelector('[name="quantity"]');
        if (input) input.value = quantityValue(input, quantity);
        const variantId = this.querySelector('[data-variant-id]')?.dataset.variantId;
        if (updateHistory) {
          const publicURL = new URL(destination);
          publicURL.searchParams.delete('section_id');
          if (variantId) {
            publicURL.searchParams.delete('option_values');
            publicURL.searchParams.set('variant', variantId);
          } else publicURL.searchParams.delete('variant');
          history.pushState({}, '', publicURL);
        }
        this.setPending(false);
        status.textContent = [
          ...this.querySelectorAll('.variant-group__selected'),
          this.querySelector('.main-product__price'),
          this.querySelector('[data-availability]')
        ].filter(Boolean).map(node => node.textContent.trim()).join(' · ');
        this.observePurchase();
        window.Shopify?.PaymentButton?.init?.();
        const focusTarget = focusId && document.getElementById(focusId);
        if (focusTarget && this.contains(focusTarget)) focusTarget.focus({ preventScroll: true });
        this.dispatchEvent(new CustomEvent('ec:variant-change', { bubbles: true, detail: { variantId } }));
      } catch (error) {
        if (error.name === 'AbortError' || sequence !== this.sequence) return;
        // Never re-enable an old variant after a failed selection request.
        status.replaceChildren();
        status.classList.add('is-error');
        status.tabIndex = -1;
        const retry = document.createElement('a');
        retry.href = destination.href;
        retry.textContent = this.dataset.errorMessage;
        status.append(retry);
        status.focus();
        this.setAttribute('aria-busy', 'false');
      }
    }

    async addToCart(event) {
      if (!window.fetch) return;
      event.preventDefault();
      const form = event.target;
      if (this.pending || this.adding || !form.reportValidity()) return;
      const variant = form.querySelector('[name="id"]');
      if (!variant?.value || variant.disabled) return;
      const data = new FormData(form);
      const submittedVariant = variant.value;
      const selectionSequence = this.sequence;
      const feedback = form.querySelector('[data-cart-feedback]');
      this.adding = true;
      this.querySelectorAll('[data-add-button]').forEach(button => { button.disabled = true; });
      const picker = this.querySelector('[data-variant-picker]');
      if (picker) picker.inert = true;
      feedback.textContent = this.dataset.loadingMessage;
      try {
        const response = await fetch(root() + 'cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: data });
        const result = await response.json();
        if (!response.ok || result.status) {
          const rejection = new Error(typeof result.description === 'string' ? result.description : this.dataset.cartError);
          rejection.confirmedRejection = true;
          throw rejection;
        }
        feedback.textContent = this.dataset.addedMessage;
        form.querySelector('[data-cart-link]').hidden = false;
        this.dispatchEvent(new CustomEvent('ec:cart-added', { bubbles: true, detail: result }));
        // A failed count refresh must never turn a successful add into a retry/add prompt.
        try {
          const cartResponse = await fetch(root() + 'cart.js');
          if (cartResponse.ok) {
            const cart = await cartResponse.json();
            document.querySelectorAll('[data-cart-count]').forEach(node => {
              node.textContent = cart.item_count;
              node.hidden = cart.item_count === 0;
            });
          }
        } catch (_) { /* The cart page remains the source of truth. */ }
      } catch (error) {
        feedback.textContent = error.confirmedRejection ? error.message : this.dataset.cartError;
        form.querySelector('[data-cart-link]').hidden = false;
      } finally {
        this.adding = false;
        if (picker) picker.inert = false;
        if (!this.pending && selectionSequence === this.sequence && this.querySelector('[name="id"]')?.value === submittedVariant) {
          this.querySelectorAll('[data-add-button]').forEach(button => { button.disabled = false; });
        }
      }
    }
  }

  class ECGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.slides = [...this.querySelectorAll('[data-media-id]')];
      this.index = 0;
      this.onClick = event => {
        const target = event.target.closest('[data-gallery-target], [data-gallery-step], [data-zoom-src], [data-close-dialog]');
        if (!target) return;
        if (target.hasAttribute('data-gallery-target')) this.go(this.slides.findIndex(slide => slide.dataset.mediaId === target.dataset.galleryTarget));
        if (target.hasAttribute('data-gallery-step')) this.go(this.index + Number(target.dataset.galleryStep));
        const dialog = this.querySelector('[data-image-dialog]');
        if (target.hasAttribute('data-zoom-src') && dialog?.showModal) {
          const image = dialog.querySelector('img');
          image.src = target.dataset.zoomSrc;
          image.alt = target.querySelector('img')?.alt || '';
          this.zoomTrigger = target;
          dialog.showModal();
        }
        if (target.hasAttribute('data-close-dialog')) dialog?.close();
      };
      this.onScroll = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => {
          const bounds = this.track.getBoundingClientRect();
          const index = this.slides.reduce((best, slide, i) => Math.abs(slide.getBoundingClientRect().left - bounds.left) < Math.abs(this.slides[best].getBoundingClientRect().left - bounds.left) ? i : best, 0);
          this.select(index);
        });
      };
      this.addEventListener('click', this.onClick);
      this.track?.addEventListener('scroll', this.onScroll, { passive: true });
      this.querySelector('dialog')?.addEventListener('close', () => this.zoomTrigger?.focus());
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.track?.removeEventListener('scroll', this.onScroll);
      cancelAnimationFrame(this.frame);
    }
    go(index) {
      if (!this.slides.length) return;
      index = (index + this.slides.length) % this.slides.length;
      const left = this.slides[index].offsetLeft - this.slides[0].offsetLeft;
      this.track.scrollTo({ left, behavior: reducedMotion() ? 'auto' : 'smooth' });
      this.select(index);
    }
    select(index) {
      if (index !== this.index) {
        this.querySelectorAll('video').forEach(video => video.pause());
        this.querySelectorAll('iframe').forEach(frame => {
          if (frame.src.includes('youtube')) frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), new URL(frame.src).origin);
          if (frame.src.includes('vimeo')) frame.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), new URL(frame.src).origin);
        });
      }
      this.index = index;
      this.querySelectorAll('[data-gallery-target]').forEach(button => button.setAttribute('aria-current', String(button.dataset.galleryTarget === this.slides[index]?.dataset.mediaId)));
      const counter = this.querySelector('[data-gallery-count]');
      if (counter) counter.textContent = (index + 1) + ' / ' + this.slides.length;
    }
  }

  customElements.define('ec-product', ECProduct);
  customElements.define('ec-gallery', ECGallery);

  // Delegation survives Shopify section reloads and product section replacement.
  document.addEventListener('click', event => {
    const quantityButton = event.target.closest('[data-quantity-button]');
    if (quantityButton) {
      const input = quantityButton.parentElement.querySelector('input[type="number"]');
      if (input) {
        const step = Number(input.step || 1) * (quantityButton.dataset.quantityButton === 'increase' ? 1 : -1);
        input.value = quantityValue(input, Number(input.value || input.min || 1) + step);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    const open = event.target.closest('[data-open-dialog]');
    if (open) {
      const dialog = document.getElementById(open.dataset.openDialog);
      if (dialog?.showModal) {
        dialog.showModal();
        dialog.dataset.returnFocus = open.id;
        open.setAttribute('aria-expanded', 'true');
        dialog.querySelector('input[type="search"]')?.focus();
      }
    }
    const close = event.target.closest('[data-close-dialog]');
    if (close) close.closest('dialog')?.close();
  });
  document.addEventListener('close', event => {
    if (!(event.target instanceof HTMLDialogElement)) return;
    const trigger = document.getElementById(event.target.dataset.returnFocus);
    trigger?.setAttribute('aria-expanded', 'false');
    trigger?.focus({ preventScroll: true });
  }, true);
  document.addEventListener('click', event => {
    if (event.target instanceof HTMLDialogElement) {
      const rect = event.target.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) event.target.close();
    }
  });
  const updateHeader = () => {
    const header = document.getElementById('shopify-section-header');
    if (header) document.documentElement.style.setProperty('--header-height', header.offsetHeight + 'px');
  };
  const initialize = () => {
    updateHeader();
    document.querySelectorAll('ec-product').forEach(product => product.observePurchase());
  };
  document.addEventListener('DOMContentLoaded', initialize);
  document.addEventListener('shopify:section:load', initialize);
  window.addEventListener('resize', () => {
    updateHeader();
    if (window.matchMedia('(min-width: 990px)').matches) document.getElementById('MobileMenu')?.close();
  }, { passive: true });
})();
